import React, { useMemo, useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';

import { IMcpFerramenta } from '../../../hooks/useMcp';
import { resumoDaDescricao, ROTULO_ESTADO, ROTULO_TIPO, TOM_ESTADO, TOM_TIPO } from '../mcpUtils';
import {
  AZUL,
  Busca,
  Cartao,
  Contador,
  Etiqueta,
  Eyebrow,
  MONO,
  Pilula,
  TEXTO,
  TEXTO_SUAVE,
} from '../ui';
import { DetalheDaFerramenta } from './DetalheDaFerramenta';

type Filtro = 'todas' | 'leitura' | 'escrita' | 'fora';
const FILTROS: { valor: Filtro; rotulo: string }[] = [
  { valor: 'todas', rotulo: 'Todas' },
  { valor: 'leitura', rotulo: 'Leitura' },
  { valor: 'escrita', rotulo: 'Escrita' },
  { valor: 'fora', rotulo: 'Fora do ar' },
];

export function FerramentasTab({ ferramentas }: { ferramentas: IMcpFerramenta[] }) {
  const [params, setParams] = useSearchParams();
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState<Filtro>('todas');

  const termo = busca.trim().toLowerCase();
  const filtradas = useMemo(
    () =>
      ferramentas.filter(f => {
        if (filtro === 'leitura' && f.escrita) return false;
        if (filtro === 'escrita' && !f.escrita) return false;
        if (filtro === 'fora' && f.estado === 'habilitada') return false;
        return (
          !termo ||
          f.nome.toLowerCase().includes(termo) ||
          f.descricao.toLowerCase().includes(termo)
        );
      }),
    [ferramentas, filtro, termo],
  );

  const escolhida =
    ferramentas.find(f => f.nome === params.get('ferramenta')) ?? filtradas[0] ?? ferramentas[0];

  const abrir = (nome: string) => {
    const p = new URLSearchParams(params);
    p.set('ferramenta', nome);
    setParams(p, { replace: true });
  };

  const secoes = [
    { titulo: 'DA CENTRAL DE PERMISSÕES', itens: filtradas.filter(f => f.nivel === 'restrita') },
    { titulo: 'GERAIS (QUALQUER SESSÃO)', itens: filtradas.filter(f => f.nivel === 'geral') },
  ];

  return (
    <Flex gap="20px" align="flex-start" wrap="wrap">
      <Cartao
        as="section"
        aria-label="Ferramentas do MCP"
        flex="1 1 300px"
        maxW={{ base: '100%', lg: '380px' }}
        minW={0}
        p="18px"
        display="flex"
        flexDirection="column"
        gap="12px"
      >
        <Flex align="center" justify="space-between" gap="8px">
          <Eyebrow>FERRAMENTAS</Eyebrow>
          <Contador>{ferramentas.length}</Contador>
        </Flex>
        <Busca valor={busca} onChange={setBusca} placeholder="Nome ou descrição" />
        <Flex gap="6px" wrap="wrap">
          {FILTROS.map(x => (
            <Pilula key={x.valor} ativa={x.valor === filtro} onClick={() => setFiltro(x.valor)}>
              {x.rotulo}
            </Pilula>
          ))}
        </Flex>

        {secoes.map(s => (
          <Flex key={s.titulo} direction="column" gap="4px">
            <Flex justify="space-between" align="center" mt="4px">
              <Eyebrow>{s.titulo}</Eyebrow>
              <Contador>{s.itens.length}</Contador>
            </Flex>
            {s.itens.length === 0 && (
              <Text fontSize="12.5px" color={TEXTO_SUAVE} py="4px">
                Nenhuma.
              </Text>
            )}
            {s.itens.map(f => {
              const aberta = escolhida?.nome === f.nome;
              return (
                <Box
                  key={f.nome}
                  as="button"
                  type="button"
                  textAlign="left"
                  onClick={() => abrir(f.nome)}
                  p="8px 10px"
                  borderRadius="14px"
                  bg={aberta ? '#DFF3FF' : 'transparent'}
                  boxShadow={aberta ? `inset 0 0 0 1px ${AZUL}33` : undefined}
                  opacity={f.estado === 'habilitada' ? 1 : 0.6}
                  _hover={aberta ? undefined : { bg: 'rgba(255,255,255,.8)' }}
                >
                  <Text
                    fontFamily={MONO}
                    fontSize="12.5px"
                    fontWeight={600}
                    color={TEXTO}
                    textDecoration={f.estado === 'removida' ? 'line-through' : undefined}
                    wordBreak="break-all"
                  >
                    {f.nome}
                  </Text>
                  <Text fontSize="12px" color={TEXTO_SUAVE} noOfLines={1}>
                    {resumoDaDescricao(f.descricao)}
                  </Text>
                  <Flex gap="4px" wrap="wrap" mt="6px">
                    <Etiqueta tom={TOM_TIPO[f.tipo]}>{ROTULO_TIPO[f.tipo].split(' ')[0]}</Etiqueta>
                    {f.estado !== 'habilitada' && (
                      <Etiqueta tom={TOM_ESTADO[f.estado]}>{ROTULO_ESTADO[f.estado]}</Etiqueta>
                    )}
                    {f.grupos.length > 0 && (
                      <Etiqueta tom="info">{f.grupos.length} grupo(s)</Etiqueta>
                    )}
                  </Flex>
                </Box>
              );
            })}
          </Flex>
        ))}
      </Cartao>

      <Box flex="999 1 520px" minW={0}>
        {escolhida ? (
          <DetalheDaFerramenta key={escolhida.nome} f={escolhida} />
        ) : (
          <Cartao p="20px 22px">
            <Text fontSize="13px" color={TEXTO_SUAVE}>
              Nenhuma ferramenta carregada.
            </Text>
          </Cartao>
        )}
      </Box>
    </Flex>
  );
}
